import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { AnchorHTMLAttributes, FC, forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Link from "next/link";
import { Button, Chip, Menu, MenuItem, Tab, Tabs, Typography } from "@mui/material";
import { Box } from "@mui/system";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import styles from "./StockLinks.module.scss";
import { BaseLink } from "./types";

/**
 * The stock page's tab strip, with the tabs that do not fit moved into a menu.
 *
 * A stock page carries around twenty tabs and gains more as we ship: Stock Report,
 * Trading Idea, Activists and Alt Data all arrive conditionally on top of the
 * permanent set, so the strip is longest exactly on the stocks we have most to say
 * about. MUI's scrollable strip technically coped, but scrolling is the wrong
 * affordance for navigation — the tabs past the edge are invisible rather than
 * merely far away, and the scroll arrow is easy enough to miss that people
 * genuinely do not find Stock Scores on a 1280px screen. A menu shows what is
 * there.
 *
 * The count is measured rather than chosen at a breakpoint, because how wide the
 * strip wants to be depends on which conditional tabs *this* stock has: a
 * breakpoint tuned for a stock with none of them is wrong for a stock with all
 * four.
 */

/**
 * Width reserved for the More button before there is one to measure.
 *
 * Used only on the first measuring pass. Erring high costs at most one tab on that
 * pass and is corrected as soon as the button is in the DOM; erring low would let
 * the strip overflow, which is the thing this component exists to prevent.
 */
const MORE_BUTTON_ESTIMATE = 110;

/**
 * How many of `widths` fit in `available`, leaving room for the More button.
 *
 * Pure and exported so the arithmetic is testable without a browser. Returns the
 * full count when everything fits — no More button is rendered in that case, so
 * its width must not be subtracted — and never returns zero, because a strip with
 * no tabs is worse than one that overflows by a few pixels.
 */
export const fitCount = (widths: number[], available: number, moreWidth: number): number => {
    if (!available || !widths.length) return widths.length;
    const total = widths.reduce((sum, width) => sum + width, 0);
    if (total <= available) return widths.length;
    let used = 0;
    let count = 0;
    for (const width of widths) {
        if (used + width > available - moreWidth) break;
        used += width;
        count += 1;
    }
    return Math.max(1, count);
};

/**
 * A next/link anchor that MUI can render as a Tab or MenuItem root.
 *
 * Tabs used to navigate with `router.push` from an onClick, so the strip was
 * buttons with no hrefs: crawlers found no links from a stock page to its other
 * tabs, and nobody could open a tab in a new window. A real anchor fixes both,
 * and next/link still navigates client-side.
 */
const TabAnchor = forwardRef<HTMLAnchorElement, AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }>(function TabAnchor({ href, ...props }, ref) {
    return <Link href={href} ref={ref} {...props}></Link>;
});

interface Props {
    links: BaseLink[];
    tabColor: string;
    /** The URL a tab links to. */
    hrefFor: (link: BaseLink) => string;
}

const DesktopTabs: FC<Props> = ({ links, tabColor, hrefFor }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const stripRef = useRef<HTMLDivElement>(null);
    const moreRef = useRef<HTMLButtonElement>(null);
    // Widths are captured once, from the pass where every tab is rendered, and
    // reused afterwards: a tab in the menu is no longer in the DOM to measure, so
    // re-measuring would ratchet the strip down one tab at a time until one was
    // left. They only change when the tab list does, which `labels` keys them on.
    const widthsRef = useRef<number[]>([]);
    const [visibleCount, setVisibleCount] = useState(links.length);
    const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);

    const labels = links.map((link) => `${link.text}${link.isNew ? "*" : ""}`).join("|");

    const recompute = useCallback(() => {
        const container = containerRef.current;
        if (!container || !widthsRef.current.length) return;
        const moreWidth = moreRef.current?.offsetWidth || MORE_BUTTON_ESTIMATE;
        setVisibleCount(fitCount(widthsRef.current, container.clientWidth, moreWidth));
    }, []);

    // Layout effects rather than effects: the measuring pass renders every tab, and
    // the correction has to land before paint or the full strip flashes.
    useLayoutEffect(() => {
        widthsRef.current = [];
        setVisibleCount(links.length);
        // `labels` is the meaningful input; `links` is a fresh array every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [labels]);

    useLayoutEffect(() => {
        const strip = stripRef.current;
        // Capture only from the pass that rendered everything; a partial strip would
        // record widths for a subset and never recover the rest.
        if (strip && !widthsRef.current.length && visibleCount === links.length) {
            widthsRef.current = Array.from(strip.querySelectorAll("[data-stock-tab]")).map((node) => (node as HTMLElement).offsetWidth);
        }
        recompute();
    }, [labels, visibleCount, links.length, recompute]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(() => recompute());
        observer.observe(container);
        return () => observer.disconnect();
    }, [recompute]);

    const visible = links.slice(0, visibleCount);
    const overflow = links.slice(visibleCount);
    const activeLink = links.find((link) => link.selected);
    const activeInOverflow = overflow.some((link) => link.selected);

    return (
        <Box ref={containerRef} className={styles.stockTabs} sx={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
            {/* The ref sits on a wrapper rather than on `Tabs`: MUI types the
                component's ref as the element its `component` prop resolves to,
                and measuring only needs an ancestor of the tabs. */}
            <Box ref={stripRef} sx={{ minWidth: 0, overflow: "hidden" }}>
                <Tabs
                    // Keyed on href rather than on an index: a tab's index moves when a
                    // conditional tab appears, and `false` is how MUI is told that the
                    // selected tab is not one of the rendered ones.
                    value={activeInOverflow || !activeLink ? false : activeLink.href}
                    variant="scrollable"
                    scrollButtons={false}
                    aria-label="stock sections"
                >
                    {visible.map((link) => (
                        <Tab
                            data-stock-tab
                            value={link.href}
                            component={TabAnchor}
                            href={hrefFor(link)}
                            sx={{ color: tabColor }}
                            key={link.text}
                            className={link.highlighted ? styles.highlightedTab : link.accent ? styles.accentTab : undefined}
                            label={
                                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                    {link.accent && <AutoAwesomeIcon sx={{ fontSize: 14, color: "#26a69a" }} />}
                                    <Typography variant="body3" sx={link.highlighted || link.accent ? { fontWeight: 700 } : undefined}>
                                        {link.text}
                                    </Typography>
                                    {/* Compact "New" tag — sized to sit next to body-3
                                    text without dominating the tab label. */}
                                    {link.isNew && <Chip label="New" size="small" color="primary" sx={{ height: 16, fontSize: 9, fontWeight: 600, "& .MuiChip-label": { px: 0.5, lineHeight: 1 } }} />}
                                </Box>
                            }
                        />
                    ))}
                </Tabs>
            </Box>

            {!!overflow.length && (
                <Button
                    ref={moreRef}
                    onClick={(event) => setMoreAnchor(event.currentTarget)}
                    sx={{
                        flexShrink: 0,
                        minWidth: "auto",
                        color: tabColor,
                        borderRadius: 0,
                        // The strip marks the current tab with an underline; the
                        // button wears the same one when it is holding it, so a page
                        // reached through the menu still shows where you are.
                        borderBottom: "2px solid",
                        borderColor: activeInOverflow ? "primary.main" : "transparent",
                    }}
                >
                    <Typography variant="body3" sx={activeInOverflow ? { fontWeight: 700 } : undefined}>
                        {activeInOverflow ? activeLink?.text : "More"}
                    </Typography>
                    <ExpandMoreIcon sx={{ fontSize: 18 }} />
                </Button>
            )}

            <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)} sx={{ maxHeight: "80vh" }}>
                {overflow.map((link) => (
                    <MenuItem key={link.text} selected={link.selected} component={TabAnchor} href={hrefFor(link)} onClick={() => setMoreAnchor(null)}>
                        <Typography variant="body3" sx={link.highlighted || link.accent ? { fontWeight: 700 } : undefined}>
                            {link.text}
                        </Typography>
                        {link.isNew && <Chip label="New" size="small" color="primary" sx={{ ml: 0.5, height: 16, fontSize: 9, fontWeight: 600, "& .MuiChip-label": { px: 0.5, lineHeight: 1 } }} />}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

export default DesktopTabs;
